import { subYears } from 'date-fns';

import { OmitNonDist, stringifyAbsoluteDate } from '@dotfile/shared/common';

import {
  CompanyTypeEnum,
  ControlCompletionAggregateEnum,
  ControlScopeEntityTypeEnum,
  ControlTypeEnum,
} from '../../../shared/models';
import { ControlModel } from '../../../shared/models/control.model';

// Base type for control configurations using OmitNonDist for polymorphic union types
export type DefaultControlConfig = OmitNonDist<
  ControlModel,
  | 'workspaceId'
  | 'id'
  | 'createdAt'
  | 'updatedAt'
  | 'workspace'
  | 'cases'
  | 'templateRules'
>;

// Default control configurations that can be easily extended
export const DEFAULT_CONTROLS: DefaultControlConfig[] = [
  // At least one UBO identified - Automatic Code Control
  {
    label: 'At least one UBO identified',
    key: 'at_least_one_ubo_identified',
    type: ControlTypeEnum.automatic_code,
    instruction:
      '<p>Automatically checks if at least one UBO is identified in the case</p>',
    archivedAt: null,
    code: `const individuals = $.input.individuals || [];
const ubo = individuals.find(ind => ind.is_beneficial_owner);
if (ubo) $.mark_control_as_completed();`,
  },
  // MLRO Sign-off - Manual Control
  {
    label: 'MLRO Sign-off',
    key: 'manual_kyb_review',
    type: ControlTypeEnum.manual,
    instruction:
      '<p>Verify the authenticity and accuracy of the business registration documents. Confirm the ownership structure and cross-check for consistency. Review the financial statements for accuracy and compliance. Check if the business appears on any sanctions or watch lists. Finally, validate the legitimacy of the business operations using external data sources.</p>',
    archivedAt: null,
  },
  // Risk is set - Automatic Logic Control (Case scope)
  {
    label: 'Risk is set',
    key: 'risk_is_set',
    type: ControlTypeEnum.automatic_logic,
    instruction: '<p>Checks if the case has a risk level assigned</p>',
    archivedAt: null,
    scope: ControlScopeEntityTypeEnum.case,
    filterJsonLogic: null,
    completionAggregate: null,
    completionJsonLogic: {
      and: [
        {
          in: [
            {
              var: 'case.risk.level',
            },
            ['low', 'medium', 'high', 'prohibited'],
          ],
        },
      ],
    },
  },
];

/**
 * Creates a dynamic control for checking if companies are older than 2 years
 * @param workspaceCreationDate The date when the workspace was created
 * @returns Control configuration for company age validation
 */
export function createCompanyOlderThanTwoYearsControl(
  workspaceCreationDate: Date,
): DefaultControlConfig {
  const twoYearsAgo = subYears(workspaceCreationDate, 2);

  return {
    label: 'Company older than 2 years',
    key: 'company_older_than_2_years',
    type: ControlTypeEnum.automatic_logic,
    instruction: '<p>Checks if the main company is older than 2 years</p>',
    archivedAt: null,
    scope: ControlScopeEntityTypeEnum.company,
    filterJsonLogic: {
      and: [
        {
          '==': [
            {
              var: 'company.type',
            },
            CompanyTypeEnum.main,
          ],
        },
      ],
    },
    completionAggregate: ControlCompletionAggregateEnum.one_of,
    completionJsonLogic: {
      and: [
        {
          '<': [
            {
              var: 'company.registration_date',
            },
            stringifyAbsoluteDate(twoYearsAgo),
          ],
        },
      ],
    },
  };
}
